/**
 * Ported from `legacy/frontend/src/TabBar.tsx` at legacy commit 2953e3f, unchanged.
 */
import { CalendarIcon, PeopleIcon, PersonIcon } from './icons';

export type View = 'sessions' | 'choir' | 'me';

/**
 * No filled variant when active: the accent carries "you are here" on its own.
 * A second signal - a shape that changes as well as a colour - is one the eye
 * has to learn, and this bar has three entries.
 */
function Icon({ view }: { view: View }) {
  if (view === 'sessions') return <CalendarIcon size={21} />;
  // Two figures against one: the pair of tabs on the right differ by whether
  // they are about everybody or about you, and that is the whole distinction
  // the icons have to carry.
  if (view === 'choir') return <PeopleIcon size={21} />;
  return <PersonIcon size={21} />;
}

// Three tabs and one subject each, and since 2026-08-22 the same three for
// both roles. It was five and three: «Спевка», «Отметиться» and «Расписание»
// are all gatherings in time and are now one screen, and the panel used to
// carry the invite link and the roster as well.
const ITEMS: { view: View; label: string }[] = [
  // «Расписание» again since 2026-08-21, the owner reversing his own call of
  // that morning. It was «Спевки» for a day on the argument that the screen
  // title while one is running is «Спевка» and a tab bar disagreeing with the
  // screen it leads to is the worse fault. What the tab actually holds is a
  // list of two kinds of gathering, and the running one is a block on top of
  // it - so it is named after the list, and the dot on the icon is what says
  // something is on.
  { view: 'sessions', label: 'Расписание' },
  // Open to a singer since 2026-08-22, read-only: who is in the choir, and
  // their own attendance beside everybody else's — the two things the
  // two-rehearsal rule is about and the app gave them no way to see. It is
  // also what stops the bar changing shape between the roles: «Я» is the third
  // tab for everybody now, and it used to be the second for half the choir.
  { view: 'choir', label: 'Хор' },
  // The singer needs this one no less than the regent: their own attendance is
  // what they come here for between rehearsals.
  { view: 'me', label: 'Я' },
];

/**
 * Three entries, in a bottom bar, and the same three for both roles.
 *
 * Labels are always visible: a singer opens this app once a week, and «Хор» and
 * «Я» as bare icons are guessable at best. A floating pill was built and
 * rejected for exactly that - see DESIGN.md.
 */
export default function TabBar({
  view,
  live,
  onSelect,
}: {
  view: View;
  /** Something is open right now, and the first tab is where it lives. */
  live: boolean;
  onSelect: (view: View) => void;
}) {
  return (
    <nav className="tabbar">
      {ITEMS.map((item) => {
        const active = view === item.view;
        return (
          <button
            key={item.view}
            className={active ? 'tabbar-item tabbar-item--active' : 'tabbar-item'}
            data-press="wash"
            aria-current={active ? 'page' : undefined}
            onClick={() => onSelect(item.view)}
          >
            <span className="tabbar-icon">
              <Icon view={item.view} />
              {/* Only ever on the first tab: it is the one that leads to the
                  thing that is running. */}
              {live && item.view === 'sessions' && <span className="tabbar-dot" />}
            </span>
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}
